import { createOffer, validateOffer } from "@/lib/actions";
import { QuoteFunctionV1, quote as quoteV1 } from "@/lib/api/v1";
import { QuoteFunctionV2, quote as quoteV2 } from "@/lib/api/v2";
import { OfferContext } from "@/lib/offer-context";
import { EventType, OfferConfig, QuoteRequest, QuoteResponse, QuoteState, ErrorResponse, ApiResponseV2, ProductConfig } from "@vertical-insure/shared";
import {ReactNode, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {getQuoteCount, resetOffer} from "@/lib/utils";

const productVersionMapping: { [key: string]: QuoteFunctionV1 | QuoteFunctionV2 } = {
  'gap-medical': quoteV1,
  'registration-cancellation': quoteV1,
  'season-interruption': quoteV1,
  'ticket-refund': quoteV1,
  'team-registration': quoteV1,
  'event-host-liability': quoteV1,
  'event-exhibitor-liability': quoteV1,
  'travel': quoteV2,
  'pet': quoteV2,
  'booking-protection': quoteV2
}

export const QuoteController = ({
  initialConfig,
  handleOfferReady,
  children
} : {
  initialConfig: OfferConfig;
  handleOfferReady: (value: boolean) => void;
  children?: ReactNode;
}) => {
  const [baseConfig, setBaseConfig] = useState(initialConfig);
  const [config, setConfig] = useState(initialConfig);
  const [quoteState, setQuoteState] = useState<QuoteState>({});
  const [offerReady, setOfferReady] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [offerId, setOfferId] = useState<string | null>(localStorage.getItem('_vi_offer_id'));
  const [loading, setLoading] = useState(false);
  const [acceptedQuotes, setAcceptedQuotes] = useState<string[]>([]);

  const form = useFormContext();

  const publishQuoteState = (acceptedQuoteIds: string[], quoteState: QuoteState) => {
    const acceptedQuotes =
      [
        ...Object.values(quoteState).flat().filter(q => 'quote_id' in q && acceptedQuoteIds.includes(q.quote_id)),
        ...Object.values(quoteState).flat()
          .map(q => 'decisions' in q ? q.decisions ?? [] : [])
          .flat()
          .map(d => d.options ?? [])
          .flat()
          .filter(o => acceptedQuoteIds.includes(o.data?.quote_id ?? ''))
      ];
    const message = {
      eventType: EventType.offer_state_change,
      quotes: acceptedQuotes,
    };

    window.parent.postMessage(message, '*');
  }

  useEffect(() => {
    const { unsubscribe } = form.watch((values, { type }) => {
      if (type === 'change') {
        const acceptedQuoteIds = values.products?.flat().map((p: { accepted_quotes: string[]; }) => p.accepted_quotes).flat() ?? [];
        publishQuoteState(acceptedQuoteIds, quoteState);
        setAcceptedQuotes(acceptedQuoteIds);
      }

      if (!formReady) {
        setFormReady(true);
      }
    });
    return () => unsubscribe()
  }, [form, quoteState]);

  useEffect(() => {
    setConfig(initialConfig);
    setBaseConfig(initialConfig);
  }, [initialConfig]);

  const updateProductConfig = (product_config: ProductConfig) => {
    setConfig({
      ...config,
      product_config
    });
  }

  useEffect(() => {
    let isMounted = true;

    const createOrUpdateQuotes = async () => {
      if (offerId) {
        setLoading(true);
        
        // validate the offerId
        const validationResponse = await validateOffer(offerId, config.version);
        if ('error' in validationResponse) {
          resetOffer();
          setOfferId(null);
          return;
        }

        const promises = Object.entries(config.product_config ?? {}).map(async ([product, requests]) => {  
          const quotes = requests.map(async (request: QuoteRequest, quoteIndex: number) => {
            const completeQuoteRequest = {
              ...request,
              offer_id: offerId,
              customer: config.customer ?? request.customer
            }

            const embeddedPayments = !!(config?.payments?.enabled && config.payments.button);

            if (isMounted) {
              const storageKey = config.unique_offer_id ? `_vi_quote:${product}:${config.unique_offer_id}:${quoteIndex}` : `_vi_quote:${product}:${quoteIndex}`;
              const quoteId = localStorage?.getItem(storageKey);

              if (quoteId) {
                completeQuoteRequest['quote_id'] = quoteId;
              }
            }

            const quoteFunction = productVersionMapping[product];

            return await quoteFunction(config.client_id, config.version, product, embeddedPayments, completeQuoteRequest);
          });
  
          return {
            product,
            quotes: await Promise.all(quotes)
          }
        });
  
        await Promise.all(promises).then(async (results) => {
          const quotes: {[key: string]: (QuoteResponse | ErrorResponse | ApiResponseV2)[]} = {};

          if (isMounted) {
            for (const i of results) {
              quotes[i.product] = i.quotes;

              i.quotes.forEach((q: QuoteResponse | ErrorResponse | ApiResponseV2, index: number) => {
                if ('quote_id' in q) {
                  const storageKey = config.unique_offer_id ? `_vi_quote:${i.product}:${config.unique_offer_id}:${index}` : `_vi_quote:${i.product}:${index}`;
                  localStorage.setItem(storageKey, q.quote_id);
                }
              });
            }
            setLoading(false);
            setQuoteState(quotes);

            if (!offerReady) {
              setOfferReady(true);
            }

            const productSubForms = Object.entries(quotes).map(([, productQuotes]) => {
              return {
                offer_declined: false,
                accepted_quotes: [],
                available_quotes: getQuoteCount(productQuotes),
              }
            });

            form.setValue('products', productSubForms);
          }
        });
      }
    }

    createOrUpdateQuotes();

    return () => {
      isMounted = false;
    }
  }, [config, offerId]);

  // Wait for the offer (e.g., all the quote requests completed), and the form is ready before we tell the page
  // controller that the entire offer is ready
  useEffect(() => {
    if (offerReady && formReady) {
      handleOfferReady(true);
    }
  }, [offerReady, formReady]);

  useEffect(() => {
    const createOfferId = async () => {
      if (!offerId) {
        resetOffer();

        const offerResponse = await createOffer(config.version);
        if (offerResponse.id) {
          setOfferId(offerResponse.id);
          localStorage.setItem('_vi_offer_id', offerResponse.id);
        } else {
          return;
        }
      }
    }
    createOfferId();
  }, [offerId, config.version]);

  const contextValue = {
    loading,
    config: baseConfig,
    quotes: quoteState,
    updateProductConfig,
    acceptedQuotes,
  }

  return (
    <OfferContext.Provider value={contextValue}>
      {children}
    </OfferContext.Provider>
  );
}
